// components/marketing/FactoryScreen.tsx
'use client';

import { useCallback, useEffect } from 'react';
import { useUser } from '@clerk/nextjs';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { ListFactoryPanel } from './ListFactoryPanel';
import { FactoryLoadingSkeleton } from './FactoryLoadingSkeleton';
import type { OrganisationRuntimeState } from '#types/types';
import { factoryLoadContext, loadFactoryChatBank } from '#lib/list/factoryNavigation';
import { isTableFocusSubject, selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';
import { ListFactoryToggle } from './ListFactoryToggle';

export function FactoryScreen({ active }: { active: boolean }) {
  const { kernel } = useKernel();
  const auth = useUser();
  const user = useKernelStore(state => state.userContext);
  const organisationId = user.isFaterAdmin || user.organisationId === URW_ORGANISATION_ID ? URW_ORGANISATION_ID : null;
  const isDashboard = useKernelStore(state => {
    const subject = selectActiveSubjectFromInventoryState(state);
    return Boolean(subject && isTableFocusSubject(subject) && subject.tableView === 'projects');
  });
  const context = JSON.stringify([auth.user?.id, user.dbUserId, user.organisationId, user.isFaterAdmin, user.isOrganisationAdmin, user.isImpersonating]);
  return <div data-factory-destination="" className="flex h-full min-h-0 flex-col bg-black text-[#ECECEC]" onBlurCapture={event => {
    if (kernel.current?.getStudioSection() !== 'factory') event.stopPropagation();
  }}>
    {/* The module homes' header (InventoryListToolbar): 24px sides and top, a 34px title row with the
        view switch at its right, then the page (its quick-action chips start 16px below). The
        organisation line moved into the Brand section, which is what it describes. */}
    <header className="mb-4 mt-6 flex h-[34px] shrink-0 items-center gap-3 px-6">
      <h1 className="min-w-0 truncate font-medium text-white" style={{ fontSize: 26, lineHeight: '32px' }}>Factory</h1>
      <span className="ml-auto">
        {isDashboard ? <ListFactoryToggle /> : <button type="button" className="inline-flex h-8 items-center rounded-full border border-[#262626] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.06]" style={{ fontSize: 14 }}
          onPointerDown={event => event.preventDefault()}
          onClick={() => kernel.current?.setStudioSection('list')}>Back to List</button>}
      </span>
    </header>
    {!auth.isLoaded ? <FactoryLoadingSkeleton /> : auth.isSignedIn && organisationId && user.dbUserId
      ? <FactoryBank key={context} organisationId={organisationId} active={active} />
      : <p className="px-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>Factory’s shared brand bank is available to authorized Westfield members.</p>}
  </div>;
}

function FactoryBank({ organisationId, active }: { organisationId: string; active: boolean }) {
  const { kernel } = useKernel();
  const context = useKernelStore(state => factoryLoadContext(state.userContext));
  const organisation = useKernelStore(state => state.inventory.Organisation.registry[organisationId]);
  const runtime = useKernelStore(state => (state.entityKernelState[`Organisation:${organisationId}`]?.state?.runtime as OrganisationRuntimeState | undefined)?.factoryLoad);
  const refresh = useCallback(async () => {
    const k = kernel.current;
    if (!k) return false;
    try {
      return Boolean(await loadFactoryChatBank(k));
    } catch { return false; }
  }, [kernel]);
  useEffect(() => {
    const k = kernel.current;
    if (!active || !k) return;
    const current = k.readEntityRuntimeState('Organisation', organisationId);
    const navigation = current.factoryNavigation;
    if (navigation?.freshBank && navigation.userId === k.getState().userContext.dbUserId && current.factoryLoad?.context === context && current.factoryLoad.status === 'ready') {
      k.patchEntityRuntimeState('Organisation', organisationId, { factoryNavigation: { ...navigation, freshBank: false } });
    } else void refresh();
  }, [active, context, organisationId]);
  const factory = organisation?.factory;
  const ready = runtime?.context === context && runtime.status === 'ready' && factory?.status === 'ready';
  return <>
    {runtime?.context === context && runtime.error && <p role="alert" className="px-6 pb-3 text-[#F28B82]" style={{ fontSize: 14 }}>{runtime.error} <button type="button" className="underline" onClick={() => void refresh()}>Retry</button></p>}
    {ready ? <ListFactoryPanel organisationId={organisationId} organisationName={organisation?.name} factory={factory} kernel={kernel} onRefresh={refresh} active={active} />
      : runtime?.context === context && runtime.status === 'error' ? <p className="px-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>Factory is unavailable.</p> : <FactoryLoadingSkeleton />}
  </>;
}
